"use client";

import { useState } from "react";
import {
  Checkbox,
  Notice,
  type Option,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { RatioResults } from "@/components/tools/accounting/ratio-results";
import { CurrencyField } from "@/components/tools/finance/shared";
import { useToolUi } from "@/components/tools/tool-ui";
import { parseNumberInput } from "@/lib/parse-number";
import {
  computeRatios,
  type Statements,
} from "@/lib/tool-kit/accounting/ratios";

/** English strings. A translated content file overrides these through `ui`. */
export const FINANCIAL_RATIO_CALCULATOR_STRINGS = {
  inputsHeading: "Your statement figures",
  inputsIntro:
    "Every field is optional. Each ratio appears as soon as the figures it needs are filled in; the rest show a dash. The example is an invented company.",
  currency: "Currency",
  plainNumbers: "No currency (plain numbers)",
  balanceHeading: "Balance sheet — end of the period",
  priorHeading: "Balance sheet — start of the period (for averages)",
  incomeHeading: "Income statement — for the period",
  marketHeading: "Share data (optional)",
  cash: "Cash and equivalents",
  securities: "Short-term investments",
  receivables: "Accounts receivable",
  inventory: "Inventory",
  currentAssets: "Total current assets",
  totalAssets: "Total assets",
  payables: "Accounts payable",
  currentLiabilities: "Total current liabilities",
  totalLiabilities: "Total liabilities",
  debt: "Interest-bearing debt",
  equity: "Shareholders' equity",
  priorReceivables: "Accounts receivable, start",
  priorInventory: "Inventory, start",
  priorPayables: "Accounts payable, start",
  priorAssets: "Total assets, start",
  priorEquity: "Shareholders' equity, start",
  revenue: "Revenue (net sales)",
  cogs: "Cost of goods sold",
  ebit: "Operating income (EBIT)",
  interest: "Interest expense",
  netIncome: "Net income",
  price: "Share price",
  shares: "Shares outstanding",
  dividends: "Total common dividends paid",
  useAverage: "Use average balances for turnover and return ratios",
  daysLabel: "Days in the year",
  days365: "365 days",
  days360: "360 days (banker's year)",
  convention:
    "Average balance = (start + end) ÷ 2. Without a start figure the end balance is used. All revenue is treated as credit sales. Textbooks differ on average versus ending balances, so the formula column names the basis used.",
  resultsHeading: "Ratios",
  fallbackNote:
    "Some ratios used end-of-period balances because the start-of-period figure was missing.",
  basisAverage: "average",
  basisEnding: "ending",
  notAvailable: "—",
  ratioColumn: "Ratio",
  formulaColumn: "Formula",
  valueColumn: "Value",
  groupLiquidity: "Liquidity",
  groupSolvency: "Solvency (leverage)",
  groupEfficiency: "Efficiency (activity)",
  groupProfitability: "Profitability",
  groupMarket: "Market",
  noteLiquidity: "Can the business pay its short-term obligations?",
  noteSolvency: "How much is financed by debt, and can it carry the interest?",
  noteEfficiency: "How quickly does it turn assets into sales and cash?",
  noteProfitability:
    "How much profit does it earn on sales, assets and equity?",
  noteMarket: "How does the market price compare with earnings and book value?",
  nameCurrentRatio: "Current ratio",
  formulaCurrentRatio: "Current assets ÷ current liabilities",
  nameQuickRatio: "Quick ratio (acid-test)",
  formulaQuickRatio:
    "(Cash + short-term investments + receivables) ÷ current liabilities",
  nameQuickRatioAlt: "Quick ratio, alternative",
  formulaQuickRatioAlt: "(Current assets − inventory) ÷ current liabilities",
  nameCashRatio: "Cash ratio",
  formulaCashRatio: "(Cash + short-term investments) ÷ current liabilities",
  nameWorkingCapital: "Working capital",
  formulaWorkingCapital: "Current assets − current liabilities",
  nameDebtToEquity: "Debt-to-equity",
  formulaDebtToEquity: "Total liabilities ÷ shareholders' equity",
  nameDebtRatio: "Debt ratio",
  formulaDebtRatio: "Total liabilities ÷ total assets",
  nameEquityMultiplier: "Equity multiplier",
  formulaEquityMultiplier: "Total assets ÷ shareholders' equity",
  nameInterestBearingDebtToEquity: "Debt-to-equity, interest-bearing debt only",
  formulaInterestBearingDebtToEquity:
    "Interest-bearing debt ÷ shareholders' equity",
  nameInterestCoverage: "Times interest earned",
  formulaInterestCoverage: "Operating income (EBIT) ÷ interest expense",
  nameAssetTurnover: "Total asset turnover",
  formulaAssetTurnover: "Revenue ÷ {basis} total assets",
  nameReceivablesTurnover: "Receivables turnover",
  formulaReceivablesTurnover: "Revenue ÷ {basis} accounts receivable",
  nameDaysSales: "Days sales outstanding",
  formulaDaysSales: "{days} ÷ receivables turnover",
  nameInventoryTurnover: "Inventory turnover",
  formulaInventoryTurnover: "Cost of goods sold ÷ {basis} inventory",
  nameDaysInventory: "Days inventory outstanding",
  formulaDaysInventory: "{days} ÷ inventory turnover",
  namePayablesTurnover: "Payables turnover",
  formulaPayablesTurnover: "Cost of goods sold ÷ {basis} accounts payable",
  nameDaysPayable: "Days payable outstanding",
  formulaDaysPayable: "{days} ÷ payables turnover",
  nameCashConversionCycle: "Cash conversion cycle (days)",
  formulaCashConversionCycle: "Days inventory + days sales − days payable",
  nameGrossMargin: "Gross margin",
  formulaGrossMargin: "(Revenue − cost of goods sold) ÷ revenue",
  nameOperatingMargin: "Operating margin",
  formulaOperatingMargin: "Operating income ÷ revenue",
  nameNetMargin: "Net profit margin",
  formulaNetMargin: "Net income ÷ revenue",
  nameReturnOnAssets: "Return on assets (ROA)",
  formulaReturnOnAssets: "Net income ÷ {basis} total assets",
  nameReturnOnEquity: "Return on equity (ROE)",
  formulaReturnOnEquity: "Net income ÷ {basis} shareholders' equity",
  nameEarningsPerShare: "Earnings per share (EPS)",
  formulaEarningsPerShare: "Net income ÷ shares outstanding",
  namePriceEarnings: "Price-earnings ratio (P/E)",
  formulaPriceEarnings: "Share price ÷ EPS",
  nameDividendPerShare: "Dividends per share",
  formulaDividendPerShare: "Total dividends ÷ shares outstanding",
  nameDividendYield: "Dividend yield",
  formulaDividendYield: "Dividends per share ÷ share price",
  namePayoutRatio: "Dividend payout ratio",
  formulaPayoutRatio: "Total dividends ÷ net income",
  nameBookValuePerShare: "Book value per share",
  formulaBookValuePerShare: "Shareholders' equity ÷ shares outstanding",
  namePriceToBook: "Price-to-book ratio",
  formulaPriceToBook: "Share price ÷ book value per share",
  nameMarketCap: "Market capitalization",
  formulaMarketCap: "Share price × shares outstanding",
  dupontHeading: "DuPont breakdown of ROE",
  dupontLine:
    "ROE = net margin × asset turnover × equity multiplier = {margin} × {turnover} × {multiplier} = {roe}",
} as const;

export type FinancialRatioCalculatorStrings =
  typeof FINANCIAL_RATIO_CALCULATOR_STRINGS;

type FieldKey = keyof Statements;
type Entries = Record<FieldKey, string>;

interface Section {
  heading: keyof FinancialRatioCalculatorStrings;
  fields: FieldKey[];
}

const SECTIONS: Section[] = [
  {
    heading: "balanceHeading",
    fields: [
      "cash",
      "securities",
      "receivables",
      "inventory",
      "currentAssets",
      "totalAssets",
      "payables",
      "currentLiabilities",
      "totalLiabilities",
      "debt",
      "equity",
    ],
  },
  {
    heading: "priorHeading",
    fields: [
      "priorReceivables",
      "priorInventory",
      "priorPayables",
      "priorAssets",
      "priorEquity",
    ],
  },
  {
    heading: "incomeHeading",
    fields: ["revenue", "cogs", "ebit", "interest", "netIncome"],
  },
  { heading: "marketHeading", fields: ["price", "shares", "dividends"] },
];

const EXAMPLE: Entries = {
  cash: "120000",
  securities: "30000",
  receivables: "150000",
  inventory: "200000",
  payables: "110000",
  currentAssets: "520000",
  totalAssets: "1400000",
  currentLiabilities: "260000",
  totalLiabilities: "760000",
  debt: "420000",
  equity: "640000",
  revenue: "1800000",
  cogs: "1100000",
  ebit: "190000",
  interest: "30000",
  netIncome: "120000",
  price: "24",
  shares: "50000",
  dividends: "36000",
  priorReceivables: "130000",
  priorInventory: "180000",
  priorPayables: "100000",
  priorAssets: "1300000",
  priorEquity: "600000",
};

function toStatements(entries: Entries): Statements {
  const out = {} as Statements;
  for (const key of Object.keys(entries) as FieldKey[]) {
    out[key] = parseNumberInput(entries[key]);
  }
  return out;
}

export default function FinancialRatioCalculatorWidget() {
  const strings = useToolUi(FINANCIAL_RATIO_CALCULATOR_STRINGS);
  const [currency, setCurrency] = useState("USD");
  const [entries, setEntries] = useState<Entries>(EXAMPLE);
  const [useAverage, setUseAverage] = useState(true);
  const [days, setDays] = useState("365");
  const dayOptions: Option[] = [
    { value: "365", label: strings.days365 },
    { value: "360", label: strings.days360 },
  ];
  const statements = toStatements(entries);
  const anyValue = Object.values(statements).some((v) => v !== null);
  const output = computeRatios(statements, {
    useAverage,
    daysInYear: Number(days),
  });

  return (
    <div className="space-y-6">
      <section className={`${CARD} space-y-5`}>
        <h2 className={WIDGET_H2}>{strings.inputsHeading}</h2>
        <p className={HINT}>{strings.inputsIntro}</p>
        <div className="grid gap-4 sm:grid-cols-3">
          <CurrencyField
            id="fr-currency"
            label={strings.currency}
            onChange={setCurrency}
            plainLabel={strings.plainNumbers}
            value={currency}
          />
          <SelectField
            id="fr-days"
            label={strings.daysLabel}
            onChange={setDays}
            options={dayOptions}
            value={days}
          />
        </div>
        <Checkbox
          checked={useAverage}
          label={strings.useAverage}
          onChange={setUseAverage}
        />
        {SECTIONS.map((section) => (
          <div className="space-y-3" key={section.heading}>
            <h3 className={WIDGET_H3}>{strings[section.heading]}</h3>
            <div className="grid gap-4 sm:grid-cols-3">
              {section.fields.map((field) => (
                <TextField
                  id={`fr-${field}`}
                  key={field}
                  label={strings[field]}
                  numeric
                  onChange={(value) =>
                    setEntries((old) => ({ ...old, [field]: value }))
                  }
                  value={entries[field]}
                />
              ))}
            </div>
          </div>
        ))}
        <p className={HINT}>{strings.convention}</p>
      </section>
      {anyValue ? (
        <RatioResults
          currency={currency}
          days={Number(days)}
          output={output}
          strings={strings}
          useAverage={useAverage}
        />
      ) : (
        <Notice>{strings.inputsIntro}</Notice>
      )}
    </div>
  );
}
